import { Button, DatePicker, Empty, Select, EchartsWithTheme, useTheme } from '@maxtropy/components';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import tittleDecoration from '../../imgs/tittleDecoration.png';
import dayjs, { Dayjs } from 'dayjs';
import { Spin } from 'antd';
import { StrategySimulationContext } from '../..';
import { useRequest } from 'ahooks';
import {
  apiV2MicrogridResolvedGetBsaPowerPredictPost,
  apiV2MicrogridResolvedGetStrategyBsaListPost,
} from '@maxtropy/intelligent-dispath-apis-v2';
import qs from 'qs';
import { getChartOption } from './chart';
import { keepTwoOrLine } from '../../utils';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';

const BsaElecPrediction = () => {
  const { strategyId, lastStartTime } = useContext(StrategySimulationContext);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [bsaId, setBsaId] = useState<number>();
  const theme = useTheme();

  // 默认选择求解开始时间 （先废弃）
  // useEffect(() => {
  //   if (lastStartTime) {
  //     setDate(dayjs(lastStartTime));
  //   }
  // }, [lastStartTime]);

  // 根据策略id获取储能站列表
  const { data: bsaList } = useRequest(
    () =>
      apiV2MicrogridResolvedGetStrategyBsaListPost({
        id: strategyId,
      }).then(res => {
        return res?.list;
      }),
    {
      ready: !!strategyId,
      refreshDeps: [strategyId],
    }
  );

  // 默认选择第一个
  useEffect(() => {
    if (!bsaList) return;
    setBsaId(bsaList?.[0]?.bsaId);
  }, [bsaList]);

  // 储能站充放电量预测
  const { data, loading } = useRequest(
    () =>
      apiV2MicrogridResolvedGetBsaPowerPredictPost({
        strategyId,
        bsaId: bsaId,
        startTime: date.startOf('day').valueOf(),
        endTime: date.endOf('day').valueOf(),
      }).then(res => {
        return res;
      }),
    {
      ready: !!strategyId && !!bsaId && !!date,
      refreshDeps: [strategyId, bsaId, date],
    }
  );

  // 图表
  const chartOption = useMemo(() => getChartOption(data), [data]);

  const chartRef = useRef<EchartsWithThemeRef>();
  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.getInstance()?.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
        <span className={styles.title}>储能站充放电量预测</span>
      </div>
      <div className={styles.content_box}>
        <div className={styles.select_box}>
          <div className={styles.select_bsa_box}>
            <span className={styles.label}>储能站：</span>
            <Select
              placeholder="请选择"
              style={{ width: 200 }}
              value={bsaId}
              options={(bsaList ?? []).map(item => ({
                label: item.bsaName,
                value: item.bsaId,
              }))}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <DatePicker
            style={{ width: 240 }}
            allowClear={false}
            picker={'date'}
            value={date}
            onChange={date => setDate(date)}
            disabledDate={current => {
              // 只允许选择 startDate ~ endDate 范围内的日期
              if (lastStartTime) {
                return current < dayjs(lastStartTime).startOf('days') || current > dayjs().add(1, 'days');
              }
              return current > dayjs().add(1, 'days');
            }}
          />
          <Button
            type="primary"
            onClick={() => {
              const params = {
                strategyId,
                bsaId,
                startTime: date.startOf('day').valueOf(),
                endTime: date.endOf('day').valueOf(),
              };
              window.open(`/api/v2/microgridResolved/exportBsaPowerPredict?${qs.stringify(params)}`);
            }}
          >
            导出
          </Button>
        </div>
        <div className={styles.card_box}>
          <div className={styles.value_eachline_box}>
            <div className={styles.label}>充电量预测：</div>
            <div className={styles.blue_value}>{keepTwoOrLine(data?.chargePredictValue)}kWh</div>
          </div>
          <div className={styles.value_eachline_box}>
            <div className={styles.label}>放电量预测：</div>
            <div className={styles.yellow_value}>{keepTwoOrLine(data?.disChargePredictValue)}kWh</div>
          </div>
        </div>
        <div className={styles.chart_box}>
          <EchartsWithTheme
            theme={theme}
            empty={{
              isShowEmpty: !data?.chargePredictValues?.length && !data?.disChargePredictValues?.length,
              emptyNode: <Empty />,
            }}
            loading={loading}
            ref={d => {
              if (d) {
                chartRef.current = d;
              }
            }}
            style={{ height: 340, width: '100%' }}
            option={chartOption}
          />
        </div>
      </div>
    </div>
  );
};

export default BsaElecPrediction;
